class RecipeCollection extends HTMLElement {
  connectedCallback() {
    this.perPage = parseInt(this.dataset.perPage, 10) || 9;
    this.visibleCount = this.perPage;

    this.cards = Array.from(this.querySelectorAll('[data-card]'));
    this.checkboxes = Array.from(this.querySelectorAll('input[type="checkbox"][data-tag]'));
    this.grid = this.querySelector('[data-grid]');
    this.emptyEl = this.querySelector('[data-empty]');
    this.loadWrap = this.querySelector('[data-loadmore-wrap]');
    this.loadBtn = this.querySelector('[data-loadmore]');
    this.resultsEl = this.querySelector('[data-results]');
    this.badge = this.querySelector('[data-active-count]');

    // Cache each card's tag list once
    this.cardTags = new Map();
    this.cards.forEach((card) => {
      this.cardTags.set(card, (card.dataset.tags || '').split(/\s+/).filter(Boolean));
    });

    this.bindEvents();
    this.readFromUrl();
    this.apply();
  }

  bindEvents() {
    this.checkboxes.forEach((cb) => {
      cb.addEventListener('change', () => {
        this.visibleCount = this.perPage;
        this.apply();
        this.writeToUrl();
      });
    });

    this.querySelectorAll('[data-clear]').forEach((btn) => {
      btn.addEventListener('click', () => this.clearAll());
    });

    if (this.loadBtn) {
      this.loadBtn.addEventListener('click', () => {
        this.visibleCount += this.perPage;
        this.apply();
      });
    }

    const openBtn = this.querySelector('[data-filter-open]');
    const closeBtn = this.querySelector('[data-filter-close]');
    if (openBtn) openBtn.addEventListener('click', () => this.classList.add('filters-open'));
    if (closeBtn) closeBtn.addEventListener('click', () => this.classList.remove('filters-open'));
  }

  // Selected tags, grouped by facet group
  getSelected() {
    const groups = {};
    this.checkboxes.forEach((cb) => {
      if (!cb.checked) return;
      const group = cb.dataset.group;
      if (!groups[group]) groups[group] = [];
      groups[group].push(cb.dataset.tag);
    });
    return groups;
  }

  // OR within a group, AND across groups
  matches(card, groups) {
    const tags = this.cardTags.get(card) || [];
    return Object.keys(groups).every((group) =>
      groups[group].some((tag) => tags.indexOf(tag) !== -1)
    );
  }

  apply() {
    const groups = this.getSelected();
    const matching = this.cards.filter((card) => this.matches(card, groups));

    this.cards.forEach((card) => { card.hidden = true; });
    matching.slice(0, this.visibleCount).forEach((card) => { card.hidden = false; });

    const noResults = matching.length === 0;
    if (this.emptyEl) this.emptyEl.hidden = !noResults;
    if (this.grid) this.grid.hidden = noResults;
    if (this.loadWrap) this.loadWrap.hidden = matching.length <= this.visibleCount;

    if (this.resultsEl) {
      const total = this.cards.length;
      const label = matching.length === 1 ? 'recipe' : 'recipes';
      this.resultsEl.textContent = matching.length === total
        ? `${total} ${label}`
        : `${matching.length} of ${total} recipes`;
    }

    if (this.badge) {
      const active = Object.keys(groups).reduce((sum, g) => sum + groups[g].length, 0);
      this.badge.textContent = active ? `(${active})` : '';
    }

    this.updateCounts(groups);
  }

  // Show how many recipes each option would yield
  updateCounts(groups) {
    this.checkboxes.forEach((cb) => {
      const countEl = this.querySelector('[data-count-for="' + cb.dataset.tag + '"]');
      if (!countEl) return;

      const probe = {};
      Object.keys(groups).forEach((g) => { probe[g] = groups[g].slice(); });

      if (!cb.checked) {
        const group = cb.dataset.group;
        probe[group] = (probe[group] || []).concat([cb.dataset.tag]);
      }

      const count = this.cards.filter((card) => this.matches(card, probe)).length;
      countEl.textContent = count;
      if (cb.parentElement) {
        cb.parentElement.classList.toggle('is-empty', count === 0 && !cb.checked);
      }
    });
  }

  clearAll() {
    this.checkboxes.forEach((cb) => { cb.checked = false; });
    this.visibleCount = this.perPage;
    this.apply();
    this.writeToUrl();
    this.classList.remove('filters-open');
  }

  // Keep filters shareable/bookmarkable via ?filter=tag,tag
  readFromUrl() {
    const params = new URLSearchParams(window.location.search);
    const raw = params.get('filter');
    if (!raw) return;
    const wanted = raw.split(',').filter(Boolean);
    this.checkboxes.forEach((cb) => {
      if (wanted.indexOf(cb.dataset.tag) !== -1) cb.checked = true;
    });
  }

  writeToUrl() {
    const selected = this.checkboxes.filter((cb) => cb.checked).map((cb) => cb.dataset.tag);
    const url = new URL(window.location.href);
    if (selected.length) {
      url.searchParams.set('filter', selected.join(','));
    } else {
      url.searchParams.delete('filter');
    }
    window.history.replaceState({}, '', url);
  }
}

customElements.define('recipe-collection', RecipeCollection);
